import { computed, reactive, ref } from 'vue';
import {
  RECUSAS_DE_ARQUIVO,
  aguardarTranscricao,
  liberarBolhas,
  mandarAoServidor,
  motivoDaFalha,
  motivoDoUpload,
  novaPergunta,
  saidaPelo,
} from './useConversaDoMotor';

// A conversa de teste no modo canal (L-13). Cada mensagem de quem testa é uma mensagem de verdade na
// caixa de teste do agente, e a resposta chega como chega a qualquer canal: o agente responde pelo
// caminho de produção, e uma pessoa da equipe pode assumir depois da transferência.
//
// A tela pergunta pelas respostas que chegaram depois da última que ela viu: a cada segundo
// enquanto espera o agente, e a cada cinco no resto do tempo, para a resposta que chega tarde — a
// da pessoa que assumiu a conversa, por exemplo. A mesma interface da `useConversaDoMotor`, para a
// tela trocar uma pela outra conforme o modo.
//
// Quem fala com o servidor é o transporte de cada tela (L-27): o `api.js` da página do link, e o do
// Playground no modo canal, que fala com a mesma caixa de teste pela sessão do painel. As mensagens
// chegam no mesmo formato nos dois, e por isso os turnos também. Um transporte tem:
// - `enviar(texto)` e `enviarComAnexos(texto, arquivos)`, que devolvem a mensagem criada
//   (`{ id, criada_em, … }`);
// - `novas(depois)`, que devolve `{ mensagens }`: as respostas depois da mensagem `depois`;
// - `subirAudio(arquivo, duracaoMs, arquivos)`, que devolve `{ mensagem }`: a nota de voz é a própria
//   mensagem, com a foto e o PDF escolhidos antes de gravar (A-16); e `audio(id)`, que devolve a
//   transcrição dela (`{ estado, texto, motivo, url }`);
// - `recomecar()`;
// - `saida(e)`, opcional, como na `useConversaDoMotor`.
export const INTERVALO_MS = 1000;
export const INTERVALO_LENTO_MS = 5000;
// Passando disto sem resposta, a página para de mostrar o "digitando" e diz que a resposta aparece
// quando chegar: no canal, a demora pode ser uma pessoa, e não erro.
export const ESPERA_MAXIMA_MS = 100000;

// A nota de voz da bolha (A-15) é o anexo da mensagem, que o servidor manda pelo proxy do
// ActiveStorage (`audio.url`): toca também depois de recarregar. A duração, quando se sabe, é a do
// gravador.
const somDoServidor = (audio, duracaoMs = null) =>
  audio?.url ? { url: audio.url, duracaoMs } : null;

export function useConversaDoCanal(transporte) {
  // { chave, id, pergunta, status, respostas: [{ chave, id, de, texto, imagens, criada_em }],
  //   falha, espera, aguardando, audio: { estado, texto, motivo }, som: { url, duracaoMs },
  //   criada_em }
  const turnos = ref([]);
  const saida = ref(null);
  const recomecando = ref(false);
  const falhaAoRecomecar = ref(false);
  const limite = ref(null);
  // A maior mensagem que a página já conhece: a consulta pede as respostas depois dela.
  let cursor = 0;
  // Até quando a consulta é a rápida.
  let prazo = 0;
  let vigiando = false;
  let encerrada = false;
  let sequencia = 0;
  let acordar = null;

  // Um envio por vez; esperar a resposta não trava o campo, como não trava em canal nenhum.
  const ocupada = computed(() =>
    turnos.value.some(turno => turno.status === 'enviando')
  );

  const novoTurno = campos => {
    sequencia += 1;
    return reactive({
      chave: `local-${sequencia}`,
      id: null,
      pergunta: null,
      respostas: [],
      ...campos,
    });
  };

  // A resposta vai para a última mensagem do visitante antes dela, e toda mensagem dele até ali
  // deixa de esperar.
  function receber(mensagens = []) {
    mensagens.forEach(mensagem => {
      if (mensagem.id <= cursor) return;
      cursor = mensagem.id;
      const anteriores = turnos.value.filter(
        turno => turno.id && turno.id < mensagem.id
      );
      let turno = anteriores[anteriores.length - 1];
      if (!turno) {
        turno = novoTurno({ status: 'respondido' });
        turnos.value.unshift(turno);
      }
      turno.respostas.push({ chave: `m-${mensagem.id}`, ...mensagem });
      anteriores.forEach(respondido => {
        if (['aguardando', 'sem_resposta'].includes(respondido.status))
          respondido.status = 'respondido';
      });
    });
  }

  function vencerEsperas() {
    if (Date.now() < prazo) return;
    turnos.value.forEach(turno => {
      if (turno.status === 'aguardando') turno.status = 'sem_resposta';
    });
  }

  // Uma espera que o envio pode encurtar: quem acabou de mandar não espera o intervalo lento.
  const cochilar = ms =>
    new Promise(resolve => {
      const relogio = setTimeout(resolve, ms);
      acordar = () => {
        clearTimeout(relogio);
        resolve();
      };
    });

  // Uma consulta por vez, até a página fechar. A rede que cai no meio não para a vigia: a próxima
  // consulta pede as mesmas respostas.
  async function vigiar() {
    if (vigiando || encerrada) return;
    vigiando = true;
    while (!encerrada) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { mensagens } = await transporte.novas(cursor);
        receber(mensagens);
      } catch (e) {
        const motivo = saidaPelo(transporte, e);
        if (motivo) {
          saida.value = motivo;
          break;
        }
      }
      vencerEsperas();
      // eslint-disable-next-line no-await-in-loop
      await cochilar(Date.now() < prazo ? INTERVALO_MS : INTERVALO_LENTO_MS);
    }
    vigiando = false;
  }

  function esperarResposta() {
    prazo = Date.now() + ESPERA_MAXIMA_MS;
    if (acordar) acordar();
    vigiar();
  }

  async function despachar(turno) {
    Object.assign(turno, { status: 'enviando', falha: null, espera: null });
    try {
      const mensagem = await mandarAoServidor(transporte, turno);
      Object.assign(turno, {
        id: mensagem.id,
        status: 'aguardando',
        criada_em: mensagem.criada_em || turno.criada_em,
      });
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) {
        saida.value = motivo;
        return;
      }
      const status = e?.response?.status;
      const dados = e?.response?.data || {};
      if (dados.erro === 'limite_do_visitante') limite.value = dados.limite;
      Object.assign(turno, {
        status: 'falhou',
        falha: motivoDaFalha(status, dados),
        falhaArquivo: dados.arquivo || null,
        espera: dados.tentar_em || null,
        aguardando: Boolean(dados.tentar_em),
      });
      if (dados.tentar_em)
        setTimeout(() => {
          turno.aguardando = false;
        }, dados.tentar_em * 1000);
      return;
    }
    esperarResposta();
  }

  // O texto, e a foto ou o PDF que vieram com ele (L-19): viram anexo da mensagem, como no widget.
  function enviar(texto, arquivos = []) {
    const pergunta = (texto || '').trim();
    if ((!pergunta && !arquivos.length) || ocupada.value || limite.value)
      return;
    const turno = novoTurno(novaPergunta(pergunta, arquivos));
    turnos.value.push(turno);
    despachar(turno);
  }

  function reenviar(turno) {
    if (ocupada.value || turno.aguardando) return;
    despachar(turno);
  }

  // A transcrição de produção da nota de voz, lida no anexo da mensagem até ficar pronta ou falhar.
  // No canal a falha não segura a mensagem: o agente a recebe sem texto, como receberia de um
  // cliente, e a bolha diz por que ele não ouviu.
  async function acompanharAudio(turno) {
    try {
      const resultado = await aguardarTranscricao(
        transporte,
        String(turno.id),
        () => encerrada
      );
      if (!encerrada) turno.audio = resultado;
    } catch (e) {
      saida.value = saidaPelo(transporte, e);
    }
  }

  // A nota de voz é a própria mensagem: o upload a cria na conversa, com o áudio anexado, e com a
  // foto e o PDF escolhidos antes de gravar (A-16), numa mensagem só.
  async function enviarAudio({ arquivo, duracaoMs }, arquivos = []) {
    if (ocupada.value || limite.value) return;
    const turno = novoTurno({
      ...novaPergunta(null, arquivos),
      status: 'enviando',
      audio: { estado: 'enviando' },
    });
    turnos.value.push(turno);
    try {
      const { mensagem } = await transporte.subirAudio(
        arquivo,
        duracaoMs,
        arquivos
      );
      Object.assign(turno, {
        id: mensagem.id,
        status: 'aguardando',
        audio: { estado: 'pendente' },
        som: somDoServidor(mensagem.audio, duracaoMs),
        criada_em: mensagem.criada_em || turno.criada_em,
      });
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) {
        saida.value = motivo;
        return;
      }
      const dados = e?.response?.data || {};
      if (dados.erro === 'limite_do_visitante') limite.value = dados.limite;
      // O arquivo que ia com a nota de voz foi recusado: nada foi criado, e o motivo é o do arquivo.
      if (RECUSAS_DE_ARQUIVO.includes(dados.erro)) {
        Object.assign(turno, {
          status: 'falhou',
          falha: dados.erro,
          falhaArquivo: dados.arquivo || null,
          audio: null,
        });
        return;
      }
      Object.assign(turno, {
        status: 'audio_falhou',
        audio: { estado: 'falhou', motivo: motivoDoUpload(e) },
      });
      return;
    }
    esperarResposta();
    acompanharAudio(turno);
  }

  // A conversa da caixa acaba, e a próxima mensagem abre outra: o agente começa sem memória.
  async function recomecar() {
    if (recomecando.value || ocupada.value) return;
    recomecando.value = true;
    falhaAoRecomecar.value = false;
    try {
      await transporte.recomecar();
      liberarBolhas(turnos.value);
      turnos.value = [];
      prazo = 0;
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) saida.value = motivo;
      else falhaAoRecomecar.value = true;
    } finally {
      recomecando.value = false;
    }
  }

  // A conversa que o servidor guardou, em ordem: a mensagem do visitante abre um turno, e as
  // respostas entram nele. A última que ainda espera volta a esperar, se não faz tempo demais.
  function carregar(conversa = [], limiteAtingido = null) {
    liberarBolhas(turnos.value);
    turnos.value = [];
    cursor = 0;
    prazo = 0;
    limite.value = limiteAtingido;
    conversa.forEach(mensagem => {
      if (mensagem.de !== 'visitante') {
        receber([mensagem]);
        return;
      }
      const turno = reactive({
        chave: `m-${mensagem.id}`,
        id: mensagem.id,
        pergunta: mensagem.texto,
        respostas: [],
        status: 'aguardando',
        audio: mensagem.audio || null,
        som: somDoServidor(mensagem.audio),
        anexos: mensagem.anexos || [],
        criada_em: mensagem.criada_em,
      });
      turnos.value.push(turno);
      if (turno.audio?.estado === 'pendente') acompanharAudio(turno);
      cursor = Math.max(cursor, mensagem.id);
      prazo = (Date.parse(mensagem.criada_em) || 0) + ESPERA_MAXIMA_MS;
    });
    // Com tudo respondido, a consulta é a lenta desde já.
    if (!turnos.value.some(turno => turno.status === 'aguardando')) prazo = 0;
    vencerEsperas();
    vigiar();
  }

  function encerrar() {
    encerrada = true;
    if (acordar) acordar();
    liberarBolhas(turnos.value);
  }

  return {
    turnos,
    saida,
    ocupada,
    recomecando,
    falhaAoRecomecar,
    limite,
    enviar,
    enviarAudio,
    reenviar,
    recomecar,
    carregar,
    encerrar,
  };
}
